import { supabase } from './supabaseClient';
import { assertGatewayRegistrationAllowed, mapRegistrationError } from '../utils/subscriptionCapacity';

const normalizeMac = (value) => (value || '').toUpperCase().replace(/[^0-9A-F]/g, '');

const formatMacForStorage = (macAddress) => {
  if (!macAddress) return undefined;
  const uuidPattern = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/i;
  if (uuidPattern.test(macAddress)) {
    return undefined;
  }
  if (/^[A-F0-9]{12}$/i.test(macAddress)) {
    return macAddress.match(/.{2}/g)?.join(':').toUpperCase();
  }
  if (/^([A-F0-9]{2}[:-]){5}[A-F0-9]{2}$/i.test(macAddress)) {
    return macAddress.toUpperCase();
  }
  const cleaned = macAddress.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
  if (cleaned.length === 12) {
    return cleaned.match(/.{2}/g)?.join(':');
  }
  return macAddress.toUpperCase();
};

export const addGateway = async (deviceId, name, macAddress, options = {}) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('User not authenticated');
  }

  const gatewayName = (name || `Gateway ${String(deviceId || '').slice(-6)}`).trim();
  const macForStorage = formatMacForStorage(macAddress);
  const normalizedDeviceId = deviceId || macForStorage || gatewayName;

  const { data: existingByDeviceId } = await supabase
    .from('gateways')
    .select('id, name, mac_address, gateway_type')
    .eq('user_id', user.id)
    .eq('device_id', normalizedDeviceId)
    .is('deleted_at', null)
    .maybeSingle();

  if (existingByDeviceId) {
    const updateData = {};
    if (gatewayName && existingByDeviceId.name !== gatewayName) {
      updateData.name = gatewayName;
    }
    if (macForStorage && existingByDeviceId.mac_address !== macForStorage) {
      updateData.mac_address = macForStorage;
    }
    if (options.gatewayType && existingByDeviceId.gateway_type !== options.gatewayType) {
      updateData.gateway_type = options.gatewayType;
    }

    if (Object.keys(updateData).length > 0) {
      const { error: updateError } = await supabase
        .from('gateways')
        .update(updateData)
        .eq('id', existingByDeviceId.id);
      if (updateError) {
        throw updateError;
      }
      return existingByDeviceId;
    }

    throw new Error(`Gateway "${gatewayName}" is already added to your account`);
  }

  if (macForStorage) {
    const { data: existingByMac } = await supabase
      .from('gateways')
      .select('id')
      .eq('user_id', user.id)
      .eq('mac_address', macForStorage)
      .is('deleted_at', null)
      .maybeSingle();
    if (existingByMac) {
      throw new Error(`Gateway with MAC ${macForStorage} is already added to your account`);
    }
  }

  try {
    await assertGatewayRegistrationAllowed();
  } catch (capacityError) {
    throw new Error(mapRegistrationError(capacityError, 'gateway'));
  }

  const gatewayData = {
    user_id: user.id,
    name: gatewayName,
    device_id: normalizedDeviceId,
  };

  if (macForStorage) {
    gatewayData.mac_address = macForStorage;
  }
  if (options.gatewayType) {
    gatewayData.gateway_type = options.gatewayType;
  }
  if (options.hardwareType) {
    gatewayData.hardware_type = options.hardwareType;
  }

  const { data: insertedGateway, error: gatewayError } = await supabase
    .from('gateways')
    .insert(gatewayData)
    .select()
    .single();

  if (gatewayError) {
    throw new Error(mapRegistrationError(gatewayError, 'gateway'));
  }

  return insertedGateway;
};

export default { addGateway };
